import React, { useState } from 'react'
import { css } from '@emotion/core'
import { Container, Box, Grid } from '@material-ui/core'

import { Theme } from '~/styles'
import { Heading, Typography, Input, Button, Header, Main } from '~/components'
import Loader from '~/components/Loader/Loader'

interface InviteCodeUIProps {
  onSubmit: (inviteCode: string) => Promise<void>
  error?: string | null
  isLoading?: boolean
}

/**
 * Reusable UI component for invite code input screens.
 * Used by both authenticated and unauthenticated invite code flows.
 */
export const InviteCodeUI: React.FC<InviteCodeUIProps> = ({
  onSubmit,
  error: externalError,
  isLoading = false,
}) => {
  const [inviteCode, setInviteCode] = useState('')
  const [internalError, setInternalError] = useState<string | null>(null)

  const displayError = externalError || internalError

  const handleNext = async () => {
    setInternalError(null)

    if (!inviteCode.trim()) {
      setInternalError('Invite code is required')
      return
    }

    await onSubmit(inviteCode.trim())
  }

  return isLoading ? (
    <>
      <Header />
      <Loader />
    </>
  ) : (
    <>
      <Header />
      <Container>
        <Box mt={10}>
          <Grid container spacing={2} justifyContent="center">
            <Grid item xs={12} md={6} lg={5}>
              <Main px={{ xs: 0 }}>
                <Heading>Explore the Firefly health plan</Heading>
                <Typography variant="subText">
                  Ready to see how healthcare should feel? Use the app to find providers, meet our
                  dedicated care teams, and chat with us.
                </Typography>
                <Box mt={4} />
                <Typography variant="subText">
                  Enter the invite code for your employer found in the open enrollment information.
                </Typography>
                <Box mt={2} />
                <Typography variant="subText" css={styles.boldText}>
                  Can&apos;t find your code?
                </Typography>
                <Typography variant="subText">Reach out to your HR team.</Typography>
                <Box mt={4} />
                <Box mb={2}>
                  <form
                    onSubmit={e => {
                      e.preventDefault()
                      handleNext()
                    }}
                  >
                    <Input
                      data-cy="inviteCode"
                      label="Invite code"
                      name="inviteCode"
                      value={inviteCode}
                      error={displayError}
                      disabled={isLoading}
                      autoComplete="off"
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        setInviteCode(e.target.value.toUpperCase())
                        if (displayError) {
                          setInternalError(null)
                        }
                      }}
                    />
                  </form>
                </Box>
                <Box my={5}>
                  <Button
                    data-cy="submit"
                    fullWidth
                    type="button"
                    css={styles.nextButton}
                    loading={isLoading}
                    onClick={handleNext}
                  >
                    Next
                  </Button>
                </Box>
              </Main>
            </Grid>
          </Grid>
        </Box>
      </Container>
    </>
  )
}

const styles = {
  nextButton: (theme: Theme) =>
    css({
      marginTop: '25%',
      [theme.mediaQueries.lg]: {
        position: 'relative',
        marginTop: '1%',
      },
    }),
  boldText: () =>
    css({
      fontWeight: 600,
    }),
}
